import React from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'
import { MobileTableCard } from './MobileTableCard'
import { useMediaQuery } from '../../hooks/useMediaQuery'

interface Column<T> {
  key: string
  title: string
  sortable?: boolean
  render?: (value: any, item: T, index: number) => React.ReactNode
  width?: string
  align?: 'left' | 'center' | 'right'
  mobileLabel?: string
  hideMobile?: boolean
}

interface TableProps<T> {
  data: T[]
  columns: Column<T>[]
  keyField: string
  isLoading?: boolean
  emptyMessage?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (field: string) => void
  onRowClick?: (item: T, index: number) => void
  selectedRows?: string[]
  onSelectRow?: (id: string) => void
  onSelectAll?: (selected: boolean) => void
  hoverable?: boolean
  striped?: boolean
  size?: 'sm' | 'md' | 'lg'
  mobileView?: 'cards' | 'scroll'
}

function Table<T extends Record<string, any>>({
  data,
  columns,
  keyField,
  isLoading = false,
  emptyMessage = 'Nenhum resultado encontrado',
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  hoverable = true,
  striped = true,
  size = 'md',
  mobileView = 'cards'
}: TableProps<T>) {
  const isMobile = useMediaQuery('(max-width: 1024px)')

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  }

  const paddingClasses = {
    sm: 'px-3 py-2',
    md: 'px-4 py-3',
    lg: 'px-6 py-4'
  }

  const handleSort = (field: string) => {
    if (!onSort) return
    onSort(field)
  }

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectAll) return
    onSelectAll(e.target.checked)
  }

  const handleSelectRow = (id: string) => {
    if (!onSelectRow) return
    onSelectRow(id)
  }

  const isAllSelected = selectedRows.length === data.length && data.length > 0
  const isIndeterminate = selectedRows.length > 0 && selectedRows.length < data.length

  if (isLoading) {
    return (
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="animate-pulse">
          <div className="border-b border-gray-200">
            <div className="flex">
              {columns.slice(0, isMobile ? 1 : columns.length).map((_, index) => (
                <div
                  key={index}
                  className={`${paddingClasses[size]} flex-1`}
                >
                  <div className="h-4 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>

          {[...Array(5)].map((_, index) => (
            <div key={index} className="border-b border-gray-200 p-4">
              <div className="h-4 bg-gray-100 rounded mb-2"></div>
              {isMobile && (
                <>
                  <div className="h-3 bg-gray-100 rounded mb-1 w-3/4"></div>
                  <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Mobile View - Cards
  if (isMobile && mobileView === 'cards') {
    return (
      <div className="space-y-3">
        {data.length === 0 ? (
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            {emptyMessage}
          </div>
        ) : (
          data.map((item, index) => {
            const isSelected = selectedRows.includes(item[keyField])

            return (
              <MobileTableCard
                key={item[keyField]}
                item={item}
                columns={columns}
                onRowClick={onRowClick}
                keyField={keyField}
                index={index}
                isSelected={isSelected}
                onSelectRow={onSelectRow}
              />
            )
          })
        )}
      </div>
    )
  }

  // Desktop View - Tabela tradicional
  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {onSelectRow && (
                <th className={`${paddingClasses[size]} w-12`}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isIndeterminate
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
              )}

              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx(
                    paddingClasses[size],
                    sizeClasses[size],
                    'font-medium text-gray-900 tracking-wider',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.width && `w-${column.width}`
                  )}
                >
                  {column.sortable ? (
                    <button
                      onClick={() => handleSort(column.key)}
                      className="group inline-flex items-center hover:text-gray-700"
                    >
                      {column.title}
                      <span className="ml-2 flex-none rounded">
                        {sortBy === column.key ? (
                          sortOrder === 'desc' ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronUp className="h-4 w-4" />
                          )
                        ) : (
                          <div className="h-4 w-4" />
                        )}
                      </span>
                    </button>
                  ) : (
                    column.title
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className={clsx(
            'bg-white divide-y divide-gray-200',
            striped && 'divide-y divide-gray-200'
          )}>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (onSelectRow ? 1 : 0)}
                  className={`${paddingClasses[size]} text-center text-gray-500`}
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const isSelected = selectedRows.includes(item[keyField])

                return (
                  <tr
                    key={item[keyField]}
                    onClick={() => onRowClick?.(item, index)}
                    className={clsx(
                      striped && index % 2 === 0 && 'bg-gray-50',
                      hoverable && 'hover:bg-gray-100',
                      onRowClick && 'cursor-pointer',
                      isSelected && 'bg-primary-50',
                      'transition-colors duration-150'
                    )}
                  >
                    {onSelectRow && (
                      <td className={paddingClasses[size]}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(item[keyField])}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                    )}

                    {columns.map((column) => {
                      const value = item[column.key]
                      const content = column.render
                        ? column.render(value, item, index)
                        : value

                      return (
                        <td
                          key={column.key}
                          className={clsx(
                            paddingClasses[size],
                            sizeClasses[size],
                            'text-gray-900',
                            column.align === 'center' && 'text-center',
                            column.align === 'right' && 'text-right'
                          )}
                        >
                          {content}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Table
